import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ScrollToTop } from "./ScrollToTop";
import { usePageMeta } from "../utils/usePageMeta";

interface CarData {
  model: string;
  brand: string;
  tipus: string;
  potencia: string;
  acceleracio: string;
  velMax: string;
  maleterAutonomia: string;
  preu: string;
  notes: string;
  info: string;
  image: string;
}

const carsData: CarData[] = [
  {
    model: "Vantage Coupé 2025",
    brand: "Aston Martin",
    tipus: "Esportiu gasolina",
    potencia: "655 CV",
    acceleracio: "3,4-3,5 s",
    velMax: "325 km/h",
    maleterAutonomia: "~200 L",
    preu: "165.000-320.000 €",
    notes: "Remodelat 2025",
    info: "Motor V8 adaptat per Aston, amb un disseny més refinat i prestacions millorades respecte a la generació anterior.",
    image: "/preferits/astonmartinvantage.jpg"
  },
  {
    model: "Revuelto",
    brand: "Lamborghini",
    tipus: "Supercar híbrid",
    potencia: "1015 CV",
    acceleracio: "2,5 s",
    velMax: "350+ km/h",
    maleterAutonomia: "Mínim",
    preu: "640.000-850.000 €",
    notes: "Producció limitada, Ad Personam",
    info: "Primer V12 híbrid de Lamborghini, on els motors elèctrics augmenten la potència i la resposta instantània del motor atmosfèric.",
    image: "/preferits/lamborghinirevuelto.webp"
  },
  {
    model: "Sesto Elemento",
    brand: "Lamborghini",
    tipus: "Supercar lleuger",
    potencia: "570 CV",
    acceleracio: "~2,6 s",
    velMax: "~322 km/h",
    maleterAutonomia: "Molt poc",
    preu: "2-3 M€+",
    notes: "Peça de museu/track car",
    info: "El nom significa \"sisè element\", el carboni, i gràcies a ell pesa menys de 1.000 kg, pensat per pista i col·leccionistes.",
    image: "/preferits/lamborghinisestoelemento.jpeg"
  },
  {
    model: "GranTurismo Modena/Trofeo",
    brand: "Maserati",
    tipus: "Gran turisme",
    potencia: "490-550 CV",
    acceleracio: "3,5-3,9 s",
    velMax: "300+ km/h",
    maleterAutonomia: "~250-300 L / 4 places",
    preu: "148.000-190.000 €",
    notes: "Folgore elèctrica ~195-200k €",
    info: "Manté l'esperit dels grans turismes clàssics italians, combinant motor potent amb un xassís pensat per confort i dinamisme.",
    image: "/preferits/maseratigranturismo.webp"
  },
  {
    model: "Roma",
    brand: "Ferrari",
    tipus: "Coupé esportiva / GT",
    potencia: "620 CV",
    acceleracio: "3,4 s",
    velMax: "320+ km/h",
    maleterAutonomia: "272-345 L",
    preu: "200.000-320.000 €",
    notes: "Opcions +30-50k sobre base",
    info: "Inspirat en l'elegància i estil de vida de la Roma dels anys 50 i 60, combina disseny clàssic amb tecnologia moderna.",
    image: "/preferits/ferrariroma.jpg"
  },
  {
    model: "F40",
    brand: "Ferrari",
    tipus: "Supercar clàssic",
    potencia: "478 CV",
    acceleracio: "4,1 s",
    velMax: "324 km/h",
    maleterAutonomia: "Testimonial",
    preu: "2-3,8 M€",
    notes: "Blue-chip collector car",
    info: "Últim model aprovat personalment per Enzo Ferrari abans de morir, i es va dissenyar com un supercar purista sense assistències electròniques.",
    image: "/preferits/ferrarif40.webp"
  },
  {
    model: "AMG GT Coupé GT63",
    brand: "Mercedes",
    tipus: "Esportiu GT",
    potencia: "585 CV",
    acceleracio: "~3,2 s",
    velMax: "~315 km/h",
    maleterAutonomia: "321-675 L",
    preu: "180.000-220.000 €",
    notes: "Ocasió 2024: ~130.000 €",
    info: "Combina prestacions d'un esportiu amb quatre portes i espai per a quatre ocupants, pensat per ús diari i viatges llargs.",
    image: "/preferits/mercedesamggtcoupe.jpg"
  },
  {
    model: "Classe G 63",
    brand: "Mercedes",
    tipus: "SUV luxe esportiu",
    potencia: "585 CV",
    acceleracio: "~4,5 s",
    velMax: "~220-240 km/h",
    maleterAutonomia: "Gran",
    preu: "~200.000 €",
    notes: "Primes altes per demanda",
    info: "SUV icònic amb tres diferencials bloquejables, heretat del model militar original dels anys 70, però amb luxe modern.",
    image: "/preferits/mercedesg63.jpg"
  },
  {
    model: "Evija",
    brand: "Lotus",
    tipus: "Hipercar elèctric",
    potencia: "2000-2039 CV",
    acceleracio: "<3 s",
    velMax: "~320 km/h",
    maleterAutonomia: "~400 km WLTP",
    preu: "~2 M€",
    notes: "Limitada a ~130 unitats",
    info: "Hipercar totalment elèctric amb motors independents per cada roda, oferint un control extremadament precís i acceleració immediata.",
    image: "/preferits/lotusevija.jpg"
  },
  {
    model: "720S",
    brand: "McLaren",
    tipus: "Supercar",
    potencia: "720 CV",
    acceleracio: "~2,8-2,9 s",
    velMax: "~341 km/h",
    maleterAutonomia: "~150 L",
    preu: "~200.000 €",
    notes: "Substituït pel 750S",
    info: "Xassís monocasc de carboni amb aerodinàmica activa que millora estabilitat i prestacions sense perdre maniobrabilitat.",
    image: "/preferits/mclaren720s.jpg"
  },
  {
    model: "M5 G90",
    brand: "BMW",
    tipus: "Berlina esportiva",
    potencia: "507 CV",
    acceleracio: "4,7 s",
    velMax: "250 km/h",
    maleterAutonomia: "~520 L",
    preu: "40.000-70.000 €",
    notes: "Estat V10 molt important",
    info: "Primer M5 amb sistema híbrid lleuger, millora acceleració i resposta sense perdre la línia esportiva característica.",
    image: "/preferits/bmwm5.avif"
  },
  {
    model: "M4 G82",
    brand: "BMW",
    tipus: "Coupé esportiu",
    potencia: "480-510+ CV",
    acceleracio: "~3,8 s",
    velMax: "250-290 km/h",
    maleterAutonomia: "~440 L",
    preu: "100.000-130.000 €",
    notes: "Ocasió: 80.000-110.000 €",
    info: "El seu xassís està derivat del GT4, fet perquè pugui rendir bé en carretera i en condicions exigents sense perdre confort.",
    image: "/preferits/bmwm4.avif"
  },
  {
    model: "430i xDrive Coupé",
    brand: "BMW",
    tipus: "Coupé esportiu",
    potencia: "245 CV",
    acceleracio: "5,7 s",
    velMax: "250 km/h",
    maleterAutonomia: "440 L",
    preu: "50.600-76.000 €",
    notes: "Ocasió: 45.000-60.000 €",
    info: "Combinació perfecta d'elegància i esportivitat amb tracció integral xDrive, motor turbo refinat i un disseny Coupé clàssic de BMW Sèrie 4.",
    image: "/preferits/G22.jpg"
  },
  {
    model: "911 Carrera S 992.2",
    brand: "Porsche",
    tipus: "Esportiu",
    potencia: "385-650+ CV",
    acceleracio: "2,5-4,0 s",
    velMax: ">300 km/h",
    maleterAutonomia: "Frunk + seients darrere",
    preu: "130.000-300.000 €+",
    notes: "Ocasió 2020+: 125-170k €",
    info: "Manté el motor darrere de l'eix posterior, una configuració única que Porsche ha perfeccionat durant més de 60 anys.",
    image: "/preferits/Porsche911.webp"
  },
  {
    model: "Taycan GTS",
    brand: "Porsche",
    tipus: "Elèctric esportiu",
    potencia: "~598-680 CV",
    acceleracio: "~3,4 s",
    velMax: "~250 km/h",
    maleterAutonomia: "~366 L totals",
    preu: "140.000-170.000 €",
    notes: "Depreciació més ràpida que 911",
    info: "Versió esportiva elèctrica amb gestió tèrmica avançada, permet acceleracions repetides sense perdre rendiment, tot mantenint el comportament característic de Porsche.",
    image: "/preferits/porschetaycan.avif"
  }
];

const getFuelColor = (tipus: string): string => {
  const lower = tipus.toLowerCase();
  if (lower.includes("elèctric")) return "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300";
  if (lower.includes("híbrid")) return "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300";
  return "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300";
};

const handleImageClick = (brand: string, model: string) => {
  const query = `${brand} ${model}`;
  window.open(`https://www.google.com/search?q=${encodeURIComponent(query)}&tbm=isch`, '_blank');
};

const handleReviewClick = (brand: string, model: string) => {
  const searchQuery = `${brand} ${model} review`;
  window.open(`https://www.google.com/search?q=${encodeURIComponent(searchQuery)}`, '_blank');
};

const parsePrice = (preu: string): number => {
  const matches = preu.match(/[\d]+(?:[.,][\d]+)*/g);
  if (!matches) return 0;

  const values = matches.map(m => parseFloat(m.replace(/\./g, "").replace(",", ".")));

  // Handle millions: if the string contains "M" multiply by 1.000.000
  const multiplier = /M€|M\+/i.test(preu) ? 1_000_000 : 1;

  const avg = values.reduce((sum, v) => sum + v, 0) / values.length;
  return avg * multiplier;
};

export const CarList = () => {
  usePageMeta({
    title: "Cotxes preferits | Cotx.es",
    description: "Selecció dels models que més m'agraden.",
  });

  const [sortBy, setSortBy] = useState<"name" | "price">("name");

  const sortedCars = useMemo(() => {
    const sorted = [...carsData];
    if (sortBy === "name") {
      sorted.sort((a, b) => `${a.brand} ${a.model}`.localeCompare(`${b.brand} ${b.model}`));
    } else {
      sorted.sort((a, b) => parsePrice(b.preu) - parsePrice(a.preu));
    }
    return sorted;
  }, [sortBy]);

  return (
    <>
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12 max-w-7xl">
          {/* Header */}
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-foreground">
                Cotxes preferits
              </h1>
              <p className="text-muted-foreground mt-2">
                {carsData.length} models
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Ordenar:</span>
              <button
                onClick={() => setSortBy("name")}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  sortBy === "name"
                    ? "bg-foreground text-background"
                    : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                }`}
              >
                A-Z
              </button>
              <button
                onClick={() => setSortBy("price")}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  sortBy === "price"
                    ? "bg-foreground text-background"
                    : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                }`}
              >
                Preu
              </button>
            </div>
          </div>

          {/* Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {sortedCars.map((car, index) => (
              <div
                key={index}
                className="relative bg-card border border-border rounded-2xl overflow-hidden hover:border-foreground hover:shadow-lg transition-all duration-300"
              >
                {/* Car Image */}
                <div className="w-full h-48 bg-gradient-to-br from-secondary to-muted flex items-center justify-center">
                  <img
                    src={car.image}
                    alt={`${car.brand} ${car.model}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>

                {/* Fuel badge - top right */}
                <div className="absolute top-4 right-4 z-10">
                  <span className={`inline-flex items-center justify-center px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm ${getFuelColor(car.tipus)}`}>
                    {car.tipus}
                  </span>
                </div>

                <div className="p-5">
                  {/* Header with name */}
                  <div className="mb-4 pb-3 border-b border-border">
                    <h3 className="text-lg font-bold text-foreground leading-tight">
                      {car.brand} {car.model}
                    </h3>
                  </div>

                  {/* Specs */}
                  <div className="space-y-2.5 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                        </svg>
                        Potència
                      </span>
                      <span className="font-semibold text-foreground">{car.potencia}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"/>
                          <polyline points="12 6 12 12 16 14"/>
                        </svg>
                        0-100 km/h
                      </span>
                      <span className="font-semibold text-foreground">{car.acceleracio}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                        </svg>
                        Vel. màx.
                      </span>
                      <span className="font-semibold text-foreground">{car.velMax}</span>
                    </div>

                    <div className="pt-2 mt-2 border-t border-border">
                      <div className="flex justify-between items-start">
                        <span className="text-muted-foreground flex items-center gap-2 shrink-0">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" x2="12" y1="2" y2="22"/>
                            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                          </svg>
                          Preu
                        </span>
                        <span className="font-bold text-foreground text-right">{car.preu}</span>
                      </div>
                    </div>

                    {car.info && (
                      <div className="pt-2 mt-2 border-t border-dashed border-border">
                        <p className="text-xs text-muted-foreground leading-relaxed">{car.info}</p>
                      </div>
                    )}
                  </div>

                  {/* Buttons */}
                  <div className="mt-4 pt-3 border-t border-border flex gap-2">
                    <button
                      onClick={() => handleImageClick(car.brand, car.model)}
                      className="flex-1 px-4 py-2.5 bg-foreground text-background rounded-xl font-semibold text-sm hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
                      aria-label={`Veure imatges de ${car.brand} ${car.model}`}
                    >
                      Imatges
                    </button>
                    <button
                      onClick={() => handleReviewClick(car.brand, car.model)}
                      className="flex-1 px-4 py-2.5 bg-card text-card-foreground border border-border rounded-xl font-semibold text-sm hover:bg-secondary transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
                      aria-label={`Veure ressenya de ${car.brand} ${car.model}`}
                    >
                      Ressenya
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <ScrollToTop />
    </>
  );
};

export default CarList;
